import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { STORAGE_KEYS, USER_ROLES, MOCK_COMPETITIONS, API_ENDPOINTS } from '../config';
import SettingsMenu from './SettingsMenu';
import './Navbar.css';
import './CompetitionHome.css';
import { apiRequest } from '../api';
import { useI18n } from '../i18n';

const CompetitionHome = () => {
  const username = localStorage.getItem(STORAGE_KEYS.username) || 'guest';
  const role = localStorage.getItem(STORAGE_KEYS.role) || USER_ROLES.user;
  const isLoggedIn = localStorage.getItem(STORAGE_KEYS.isLoggedIn) === 'true';
  const registryUrl = localStorage.getItem(STORAGE_KEYS.registryUrl) || '—';
  const navigate = useNavigate();
  const { t, basePath } = useI18n();
  const [keyword, setKeyword] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [isMenuOpen, setIsMenuOpen] = useState(false); // 新增折叠状态
    const handleLogout = () => {
      localStorage.removeItem(STORAGE_KEYS.isLoggedIn);
      localStorage.removeItem(STORAGE_KEYS.username);
      localStorage.removeItem(STORAGE_KEYS.accessToken);
      localStorage.removeItem(STORAGE_KEYS.registryUrl);
      localStorage.removeItem(STORAGE_KEYS.role);
      navigate(`${basePath}/leaderboard`);
    };

    const closeMenu = () => setIsMenuOpen(false);

    const handleToggleMenu = (e) => {
      e.stopPropagation();
      setIsMenuOpen(prev => !prev);
    };

    // 点击页面其他区域时关闭菜单
    React.useEffect(() => {
      if (!isMenuOpen) return;
      
      const handleClickOutside = (e) => {
        // 如果点击的是菜单内部,不关闭
        const navLinks = document.querySelector('.nav-links');
        const menuToggle = document.querySelector('.menu-toggle');
        if (navLinks?.contains(e.target) || menuToggle?.contains(e.target)) {
          return;
        }
        setIsMenuOpen(false);
      };
      
      // 延迟添加监听器,避免与当前点击事件冲突
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }, [isMenuOpen]);

    const roleLabel = role === USER_ROLES.siteAdmin
      ? t('role.siteAdmin')
      : role === USER_ROLES.competitionAdmin
        ? t('role.competitionAdmin')
        : t('role.user');

    const getStatusLabel = (status, fallback) => t(`competition.status.${status}`) || fallback || status;
  
  // 新增状态
  const [competitionsData, setCompetitionsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // 获取比赛列表
  React.useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        // 使用配置中的 endpoint
        const data = await apiRequest(API_ENDPOINTS.allCompetitions);
        //const data = []
        setCompetitionsData(Array.isArray(data) ? data : []); 
      } catch (err) {
        console.error('Failed to fetch competitions:', err);
        setCompetitionsData(MOCK_COMPETITIONS);
      } finally {
        setLoading(false);
      }
    };
    fetchCompetitions();
  }, []);

  // 申请加入比赛
  const handleJoinCompetition = async (competitionId) => {
    if (!isLoggedIn) return;
    try {
      // 使用配置中的 endpoint
      await apiRequest(API_ENDPOINTS.joinCompetition, {
        method: 'POST',
        body: JSON.stringify({ competition_id: competitionId })
      });
      alert('已申请参加比赛');
    } catch (err) {
      alert('申请失败: ' + err.message);
    }
  };

  const competitions = useMemo(() => {
    const source = competitionsData.length > 0 ? competitionsData : MOCK_COMPETITIONS;
    if (!keyword) return source;
    const lower = keyword.toLowerCase();
    return source.filter((c) =>
      c.name.toLowerCase().includes(lower) || (c.description && c.description.toLowerCase().includes(lower))
    );
  }, [keyword, competitionsData]);

  const myCompetitions = competitions.filter((c) => c.isMember);
  const openCompetitions = competitions.filter((c) => !c.isMember);

  return (
    <div className="competition-home">
      <nav className="navbar">
        <div className="nav-brand">
          <img className="nav-logo" src="/asynscale_ai_logo.png" alt="AsynScale AI Logo" />
          <span className="nav-title">Bench2Drive Leaderboard</span>
        </div>
        
        {/* 汉堡菜单按钮 */}
        <button className="menu-toggle" onClick={handleToggleMenu} aria-label="Toggle menu">
          {isMenuOpen ? '✕' : '☰'}
        </button>

        <div className={`nav-links ${isMenuOpen ? 'open' : ''}`} onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
          <Link to={`${basePath}/leaderboard`} className="nav-link" onClick={closeMenu}>{t('nav.home')}</Link>
          <Link to={`${basePath}/leaderboard/competitions`} className="nav-link active" onClick={closeMenu}>{t('nav.competitions')}</Link>
          <SettingsMenu />
          {!isLoggedIn ? (
            <Link to={`${basePath}/leaderboard/login`} className="nav-link nav-login" onClick={closeMenu}>{t('nav.login')}</Link>
          ) : (
            <div className="user-menu">
              <div className="user-menu-trigger">👤 {username}</div>
              <div className="user-menu-panel">
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.username')}</span>
                  <span>{username}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.role')}</span>
                  <span>{roleLabel}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.registry')}</span>
                  <span>{registryUrl}</span>
                </div>
                <button className="user-menu-logout" onClick={() => { handleLogout(); closeMenu(); }}>{t('user.logout')}</button>
              </div>
            </div>
          )}
          
        </div>
      </nav>
      <div className="competition-hero">
        <h1>{t('competition.title')}</h1>
        <div className="competition-actions">
          <div className="search-group">
            <input
              type="text"
              placeholder={t('competition.search')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <button className="search-btn" aria-label="搜索">⌕</button>
          </div>
          {role === USER_ROLES.siteAdmin && (
            <button className="primary-btn">创建新比赛</button>
          )}
          <div className="view-toggle pill">
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              aria-label="列表模式"
              title="列表模式"
            >
              ☰
            </button>
            <button
              className={viewMode === 'card' ? 'active' : ''}
              onClick={() => setViewMode('card')}
              aria-label="卡片模式"
              title="卡片模式"
            >
              ▦
            </button>
          </div>
        </div>
      </div>

      {loading ? <div className="section">加载中...</div> : (
        <>
          <section className="section">
            <div className="section-header">
              <h2>{t('competition.my')}</h2>
            </div>
            <div className={`competition-grid ${viewMode}`}>
              {myCompetitions.map((c) => (
                <div key={c.id} className="competition-card">
                  <div className="card-header">
                    <h3>{c.name}</h3>
                    <span className={`status-badge status-${c.status}`}>{getStatusLabel(c.status, c.statusLabel)}</span>
                  </div>
                  <p className="card-desc">{c.description}</p>
                  <div className="card-meta">
                    <span>管理员：{c.owner}</span>
                    <span>报名截止：{c.deadline}</span>
                  </div>
                  <div className="card-actions">
                    <Link to={`${basePath}/leaderboard/competitions/${c.id}`} className="secondary-btn">{t('competition.enter')}</Link>
                    <button className="ghost-btn">{t('competition.teamManage')}</button>
                  </div>
                </div>
              ))}
            </div>
            {myCompetitions.length === 0 && (
              <div className="empty-state">
                <span>暂未加入任何比赛</span>
              </div>
            )}
          </section>

          <section className="section">
            <div className="section-header">
              <h2>{t('competition.all')}</h2>
            </div>
            <div className={`competition-grid ${viewMode}`}>
              {openCompetitions.map((c) => (
                <div key={c.id} className="competition-card">
                  <div className="card-header">
                    <h3>{c.name}</h3>
                    <span className={`status-badge status-${c.status}`}>{getStatusLabel(c.status, c.statusLabel)}</span>
                  </div>
                  <p className="card-desc">{c.description}</p>
                  <div className="card-meta">
                    <span>管理员：{c.owner}</span>
                    <span>报名截止：{c.deadline}</span>
                  </div>
                  <div className="card-actions">
                    <button
                      className="primary-btn"
                      onClick={() => handleJoinCompetition(c.id)}
                    >
                      {t('competition.apply')}
                    </button>
                    <Link to={`${basePath}/leaderboard/competitions/${c.id}`} className="ghost-btn">{t('competition.view')}</Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default CompetitionHome;
